import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { COLORS, COLORS_DARK } from '../../constants';
import useResponsiveChart from '../../hooks/useResponsiveChart';
import CustomTooltip from '../CustomTooltip';
import CustomLegend from '../CustomLegend';
import ExplanationCard from '../ExplanationCard';

const LineChartView = ({ selectedMethod, selectedModel, chartData, selectedLanguages }) => {
  const { isDarkMode } = useTheme();
  const colors = isDarkMode ? COLORS_DARK : COLORS;
  const { chartHeight, chartMargin, axisLabelFontSize, tickFontSize } = useResponsiveChart();

  // Convert chartData format to match original (documents -> docCount)
  const lineData = chartData.map(item => ({
    docCount: item.documents,
    ...Object.fromEntries(
      selectedLanguages.map(lang => [lang, item[lang]])
    )
  }));

  return (
    <div className="mb-8">
      <ExplanationCard title="Performance Trends Explanation">
        <p><strong>Line Chart:</strong> Shows how performance changes as the number of documents increases from 0 to 10.</p>
        <p><strong>Each Line:</strong> Represents a different language, with colors distinguishing between them.</p>
        {/* <p><strong>Interpretation:</strong> Upward trends indicate that adding more documents improves performance, while downward trends suggest diminishing returns or overfitting.</p> */}
        {/* <p><strong>Optimal Points:</strong> Look for the highest points on each line to identify the optimal document count for each language.</p> */}
      </ExplanationCard>

      <h3 className="text-3xl font-bold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>
        Performance Trends Over Document Count
      </h3>
      <p className="text-lg text-center mb-8" style={{ color: 'var(--text-secondary)' }}>
        Analyzing {selectedMethod} method for {selectedModel} model.
      </p>

      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart data={lineData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="4 4" stroke={isDarkMode ? '#333333' : '#e0e7ff'} />
            <XAxis
              dataKey="docCount"
              label={{
                value: 'Number of Documents',
                position: 'bottom',
                offset: 40,
                fill: isDarkMode ? '#a3a3a3' : '#4a5568',
                fontSize: axisLabelFontSize,
                fontWeight: 'bold'
              }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              padding={{ left: 15, right: 15 }}
              interval="preserveStartEnd"
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <YAxis
              label={{
                value: 'Performance Score',
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                fill: isDarkMode ? '#a3a3a3' : '#4a5568',
                fontSize: axisLabelFontSize,
                fontWeight: 'bold'
              }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              domain={['dataMin - 10', 'dataMax + 10']}
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              content={<CustomLegend />}
              wrapperStyle={{ paddingTop: '20px' }}
            />
            {selectedLanguages.map(lang => (
              <Line
                key={lang}
                type="monotone"
                dataKey={lang}
                stroke={colors[lang]}
                strokeWidth={3}
                connectNulls={true}
                activeDot={{
                  r: 8,
                  stroke: colors[lang],
                  strokeWidth: 3,
                  fill: isDarkMode ? '#171717' : '#fff',
                  cursor: 'pointer'
                }}
                name={lang}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LineChartView;
